import React, { useState } from "react";
import AddIcon from "@mui/icons-material/Add";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import { Box, Button, TextField, IconButton, Typography } from "@mui/material";
import { useTheme, alpha } from "@mui/material/styles";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import ReactGA from 'react-ga4';
import { tracker } from '../../../../analytics';

const AddSignature = ({ onAdd, onCancel, existingEmails = [] }) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const currentUser = useSelector((state) => state.auth.user.user);
  const [email, setEmail] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [error, setError] = useState("");

  const canAddSelf = currentUser?.email && !existingEmails.includes(currentUser.email);

  const handleAddMyself = () => {
    setEmail(currentUser.email);
    setFirstName(currentUser.first_name || "");
    setLastName(currentUser.last_name || "");
    setError("");
  };

  // Fonction pour valider l'email
  const validateEmail = (email) => {
    const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return regex.test(email);
  };

  // Fonction pour ajouter le signataire
  const handleAdd = () => {
    if (!validateEmail(email)) {
      setError("Adresse email invalide"); // Affiche le message d'erreur
      return;
    }
    setError(""); // Efface l'erreur si l'email est valide
    onAdd(email, firstName, lastName); // Ajoute l'email et les noms
    ReactGA.event({
      category: 'Signature',
      action: 'Add Signatory',
      label: 'Added signatory via email input'
    });
    tracker.event({
      category: 'Signature',
      action: 'Add Signatory',
      label: 'Added signatory via email input'
    });

    setEmail(""); // Réinitialise les champs après l'ajout
    setFirstName("");
    setLastName("");
  };

  return (
    <Box
      sx={{
        mt: 2,
        p: 2,
        background: theme.palette.background.paper,
        borderRadius: 2,
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
        padding: 0,
        margin: 0,
      }}
    >
      {/* Titre de la section avec le bouton de retour */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "flex-start",
          mb: 2,
        }}
      >
        <IconButton onClick={onCancel} sx={{ color: theme.palette.text.primary }}>
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h6" sx={{ fontWeight: 600, color: theme.palette.text.primary }}>
          Information du signataire
        </Typography>
      </Box>

      {/* Add myself button */}
      {canAddSelf && (
        <Button
          variant="outlined"
          size="small"
          startIcon={<PersonAddIcon sx={{ fontSize: 16 }} />}
          onClick={handleAddMyself}
          sx={{
            mb: 2,
            textTransform: 'none',
            borderRadius: '10px',
            fontWeight: 500,
            fontSize: '0.82rem',
            borderColor: alpha(theme.palette.primary.main, 0.4),
            color: theme.palette.primary.main,
            '&:hover': {
              borderColor: theme.palette.primary.main,
              backgroundColor: alpha(theme.palette.primary.main, 0.06),
            },
          }}
        >
          {t("signature.addMyself")}
        </Button>
      )}

      <TextField
        fullWidth
        label="Email du signataire"
        placeholder="Entrez l'email"
        variant="outlined"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        error={!!error} // Active le style d'erreur si error n'est pas vide
        helperText={error} // Affiche le message d'erreur
        sx={{
          mb: 2,
          "& .MuiOutlinedInput-root": {
            "& fieldset": {
              borderRadius: "12px",
            },
          },
          "& input:-webkit-autofill, & input:-webkit-autofill:hover, & input:-webkit-autofill:focus": {
            WebkitBoxShadow: `0 0 0 100px ${theme.palette.background.paper} inset`,
            WebkitTextFillColor: theme.palette.text.primary,
            caretColor: theme.palette.text.primary,
          },
        }}
      />

      {/* Champ d'entrée pour le prénom */}
      <TextField
        fullWidth
        label="Prénom du signataire"
        placeholder="Entrez le prénom"
        variant="outlined"
        value={firstName}
        onChange={(e) => setFirstName(e.target.value)}
        sx={{
          mb: 2,
          "& .MuiOutlinedInput-root": {
            "& fieldset": {
              borderRadius: "12px",
            },
          },
          "& input:-webkit-autofill, & input:-webkit-autofill:hover, & input:-webkit-autofill:focus": {
            WebkitBoxShadow: `0 0 0 100px ${theme.palette.background.paper} inset`,
            WebkitTextFillColor: theme.palette.text.primary,
            caretColor: theme.palette.text.primary,
          },
        }}
      />

      {/* Champ d'entrée pour le nom */}
      <TextField
        fullWidth
        label="Nom du signataire"
        placeholder="Entrez le nom"
        variant="outlined"
        value={lastName}
        onChange={(e) => setLastName(e.target.value)}
        sx={{
          mb: 2,
          "& .MuiOutlinedInput-root": {
            "& fieldset": {
              borderRadius: "12px",
            },
          },
          "& input:-webkit-autofill, & input:-webkit-autofill:hover, & input:-webkit-autofill:focus": {
            WebkitBoxShadow: `0 0 0 100px ${theme.palette.background.paper} inset`,
            WebkitTextFillColor: theme.palette.text.primary,
            caretColor: theme.palette.text.primary,
          },
        }}
      />

      {/* Bouton pour ajouter le signataire */}
      <Button
        variant="contained"
        startIcon={<AddIcon />}
        onClick={handleAdd} // Utilise la fonction avec validation
        sx={{
          background: theme.palette.text.primary,
          color: theme.palette.mode === 'dark' ? '#000' : '#fff',
          borderRadius: 2,
          mt: 2,
          width: "100%",
        }}
      >
        Ajouter
      </Button>
    </Box>
  );
};

export default AddSignature;